import { localRegions, stateRegions } from "shared/imports/regionConstants"

/**
 * This function takes in an option and decides whether the clearableValue: false
 * key value pair should be added to the option
 * This allows to render options in a select that are not deletable
 * Must be declared as a regular JavaScript function to get this context properly
 * @param {object} option - An option to pass to the select
 * @returns {object} - The option, with a potential additional clearableValue: false k/v pair
 */
export function setClearableFilter(option) {
    if (this.disableRemoveOption && option[this.disableRemoveOptionKey]) {
        return { ...option, clearableValue: false }
    }

    return option
}

/**
 * Filter custom tag objects based on an attribute that signify whether the tag should be displayed on specific pages.
 * @param {Object} tagNameDict contains the custom tags we wish to filter
 * @param {Object} displayBool the name of the boolean on which we want our returned tags to be true (e.g. the display_on_profile bool)
 * @returns {Object} the valid subset of tags
 */
export const filterCustomTagsByAttribute = (tagNameDict, displayBool) => {
    const tags = []
    Object.keys(tagNameDict).forEach((key) => {
        const tag = tagNameDict[key]
        if (tag[displayBool]) {
            tags.push({
                description: tag.description,
                name: tag.name,
                isRequired: tag.is_required,
                tagKey: key,
                tagType: tag.tag_type,
                optionsNew: tag.options_new,
                ...tag,
            })
        }
    })
    return tags
}

// given a user's currentRegionsValues can they see the federal region
export const canSeeFederal = (currentRegionsValues) =>
    currentRegionsValues.includes(DjangIO.app.models.SupportedRegion.federal.value)

// given a user's currentRegionsValues can they see the all states in the state region
export const canSeeAllStates = (currentRegionsValues) =>
    stateRegions.every((state) => currentRegionsValues.includes(state))

// given a user's currentRegionsValues can they ONLY see the federal region
export const isFederalRegion = (currentRegionValues) =>
    canSeeFederal(currentRegionValues) && currentRegionValues.length === 1

/**
 * Helper function which determines whether a region is valid for the Bills
 * A region is valid if it is in a user's current_region_values
 * OR if all of it's child_regions are in a user's current_region_values
 * OR if the user's region is federal and the region is a state
 * @param {Object} region enum item associated with region
 * @returns {boolean} whether the input region is valid
 */
export const isValidRegionForBills = (region) =>
    (region.child_regions &&
        region.child_regions.every((childRegionId) => Userdata.current_regions_values.includes(childRegionId))) ||
    Userdata.current_regions_values.includes(region.value) ||
    // we always want to show states in federal region because we filter by sponsor
    (stateRegions.includes(region.value) && isFederalRegion(Userdata.current_regions_values))

/**
 * Helper function which determines whether a region is not a parent of all other current regions
 * There are three cases covered here
 * 1. if a user is in the Entire US region, then return true for all_regions region
 * 2. if a user is in the Federal region, then return true for the federal region
 * 3. if a user is in the US States region, then return true for the states region
 * @param {Object} region enum item associated with region
 * @returns {Boolean} whether the input region is valid
 */
export const isNotParentRegion = (region) => {
    if (canSeeFederal(Userdata.current_regions_values) && canSeeAllStates(Userdata.current_regions_values)) {
        return region.value !== DjangIO.app.models.SupportedRegion.all_regions.value
    }

    if (canSeeFederal(Userdata.current_regions_values) && !canSeeAllStates(Userdata.current_regions_values)) {
        return region.value !== DjangIO.app.models.SupportedRegion.federal.value
    }

    if (!canSeeFederal(Userdata.current_regions_values) && canSeeAllStates(Userdata.current_regions_values)) {
        return region.value !== DjangIO.app.models.SupportedRegion.states.value
    }

    return true
}

/**
 * If the current region is the federal region, we want to add the federal region
 * to the filter options. We want this option to be the last option.
 *
 * @param {Array<SupportedRegion>} options
 * @returns Array<SupportedRegion>
 */
export const addFedOptionForFedRegion = (options) =>
    isFederalRegion(Userdata.current_regions_values)
        ? [...options, DjangIO.app.models.SupportedRegion.federal]
        : options

/**
 * Gets the enum options for the BillSupportedRegions EnumSelect component
 * @returns {Object Array} enum options for the BillSupportedRegions EnumSelect component
 */
export const getBillSupportedRegionOptions = () =>
    addFedOptionForFedRegion(
        DjangIO.app.models.SupportedRegion.region_supported_items()
            .filter((region) => DjangIO.app.models.QuorumDataType.bill.supported_regions.includes(region.value))
            .filter(isValidRegionForBills)
            .filter(isNotParentRegion)
            .sort(DjangIO.orderSort),
    )

/**
 * Gets the enum options for the BillGlobalRegionsFilter EnumSelect component.
 * Only returns regions where user has permission to view legislative data.
 * @returns {Object Array} enum options for the BillGlobalRegionsFilter EnumSelect component
 */
export const getBillGlobalRegionOptions = () => {
    const allowedRegions = Userdata.Permissions.qp_legislative_data
    const hasState = stateRegions.some((region) => allowedRegions.includes(region))

    const regions = DjangIO.app.models.SupportedRegion.items().filter(
        (region) =>
            allowedRegions.includes(region.value) &&
            !localRegions.includes(region.value) &&
            region.value !== DjangIO.app.models.Region.global_region.value,
    )

    if (hasState && !regions.includes(DjangIO.app.models.SupportedRegion.states)) {
        regions.push(DjangIO.app.models.SupportedRegion.states)
    }

    return regions.sort((a, b) => (a.region_name < b.region_name ? -1 : 1))
}

/**
 * Gets the enum options for the CommitteeSupportedRegions EnumSelect component
 * @returns {Object Array} enum options for the CommitteeSupportedRegions EnumSelect component
 */
export const getCommitteeSupportedRegionOptions = () =>
    addFedOptionForFedRegion(
        DjangIO.app.models.SupportedRegion.region_supported_items()
            .filter((region) => DjangIO.app.models.QuorumDataType.committee.supported_regions.includes(region.value))
            .filter(isValidRegionForBills)
            .filter(isNotParentRegion)
            .sort(DjangIO.orderSort),
    )

/**
 * Gets the enum options for the EventSupportedRegions EnumSelect component
 * @returns {Object Array} enum options for the EventSupportedRegions EnumSelect component
 * Copied from the above committee getter.
 */
export const getEventSupportedRegionOptions = () =>
    addFedOptionForFedRegion(
        DjangIO.app.models.SupportedRegion.region_supported_items()
            .filter((region) => DjangIO.app.models.QuorumDataType.event.supported_regions.includes(region.value))
            .filter(isValidRegionForBills)
            .filter(isNotParentRegion)
            .sort(DjangIO.orderSort),
    )

/**
 * Gets the enum options for the RegulationGlobalRegionsFilter EnumSelect component.
 * Only returns regions where user has permission to view regulations data.
 * @returns {Object Array} enum options for the RegulationGlobalRegionsFilter EnumSelect component
 */
export const getRegulationGlobalRegionOptions = () => {
    const allowedRegions = Userdata.Permissions.qp_regulations
    const hasState = stateRegions.some((region) => allowedRegions.includes(region))

    const regions = DjangIO.app.models.SupportedRegion.items().filter(
        (region) =>
            allowedRegions.includes(region.value) &&
            !localRegions.includes(region.value) &&
            region.value !== DjangIO.app.models.Region.global_region.value,
    )

    if (hasState && !regions.includes(DjangIO.app.models.SupportedRegion.states)) {
        regions.push(DjangIO.app.models.SupportedRegion.states)
    }

    return regions
        .sort((a, b) => (a.region_name < b.region_name ? -1 : 1))
        .map((region) => ({
            value: region.value,
            label: region.region_name,
        }))
}

/**
 * Gets the enum options for the BillLanguageFilter EnumSelect component
 * @returns {Object Array} enum options for the BillLanguageFilter EnumSelect component
 */
export const getBillLanguageFilterOptions = () => {
    const allowedRegions = DjangIO.app.models.SupportedRegion.region_supported_items()
        .filter((region) => DjangIO.app.models.QuorumDataType.bill.supported_regions.includes(region.value))
        .filter(isValidRegionForBills)
        .filter((region) => Userdata.Permissions.qp_legislative_data.includes(region.value))
        .map((region) => region.value)
    const hasState = stateRegions.some((region) => allowedRegions.includes(region))

    const languages = DjangIO.app.bill.types.language.BillLanguage.items().filter((language) =>
        language.supported_regions.some((region) => allowedRegions.includes(region)),
    )

    if (hasState && !languages.includes(DjangIO.app.bill.types.language.BillLanguage.english)) {
        languages.push(DjangIO.app.bill.types.language.BillLanguage.english)
    }

    return languages
}

/**
 * Will set slice to the field specified, and set slice to null upon invalid or empty selection.
 *
 * This function is intended to be used as the customChange prop for Select components that simply
 * require an ID to be set in the Redux store.
 *
 * @param {Function} changeFn usually called this.props.change
 * @param {Object} obj the object selected
 * @param {String} sliceName the name of the Redux slice to write to
 * @param {String} fieldName the name of field to access in obj. Defaults to "id"
 */
export const setOrClearFormField = ({ changeFn, obj, sliceName, fieldName = "id" }) => {
    if (obj && obj.id !== undefined) {
        // e.g. 0 is a potential valid ID that is falsey.
        changeFn(sliceName, obj[fieldName])
    } else {
        changeFn(sliceName, null)
    }
}
